'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Download, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { downloadFile } from '@/contexts/media/utils/download';
import { sanitizeFilenameSegment } from '@/contexts/media/utils/filename';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';

interface VideoPlayerBaseProps {
  videoUrl: string;
  bookTitle?: string;
}

interface VideoPlayerModalProps extends VideoPlayerBaseProps {
  variant?: 'modal';
  isOpen: boolean;
  onClose: () => void;
}

interface VideoPlayerInlineProps extends VideoPlayerBaseProps {
  variant: 'inline';
  className?: string;
}

type VideoPlayerProps = VideoPlayerModalProps | VideoPlayerInlineProps;

export function VideoPlayer(props: VideoPlayerProps) {
  const t = useTranslations('illustrations.viewer');

  // Hooks must run unconditionally — declare here, then branch on variant.
  // `videoUrl` exists on both variants; `bookTitle` may be undefined.
  const { videoUrl, bookTitle } = props;

  const handleDownload = useCallback(async () => {
    const base = bookTitle ? sanitizeFilenameSegment(bookTitle) : 'book';
    const filename = `${base}.mp4`;
    try {
      await downloadFile(videoUrl, filename);
    } catch (e) {
      console.error('[VideoPlayer] Failed to download video', e);
    }
  }, [videoUrl, bookTitle]);

  if (props.variant === 'inline') {
    return (
      <div className={props.className ?? 'w-full h-full bg-black'}>
        <video
          src={mediaSrc(videoUrl)}
          controls
          autoPlay
          className="w-full h-full object-contain"
        >
          {t('videoError')}
        </video>
      </div>
    );
  }

  // Modal variant (default)
  const { isOpen, onClose } = props;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-testid="video-player-dialog"
        className="max-w-4xl w-full p-0 overflow-hidden"
        showCloseButton={false}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <DialogTitle className="font-semibold truncate pr-4">{bookTitle}</DialogTitle>
          <div className="flex items-center gap-2">
            <Button
              data-testid="video-download-button"
              variant="outline"
              size="sm"
              onClick={handleDownload}
            >
              <Download className="w-4 h-4 mr-1" />
              {t('videoDownload')}
            </Button>
            <Button
              data-testid="video-close-button"
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Video player */}
        <div className="relative bg-black aspect-video">
          <video
            src={mediaSrc(videoUrl)}
            controls
            autoPlay
            className="w-full h-full"
          >
            {t('videoError')}
          </video>
        </div>
      </DialogContent>
    </Dialog>
  );
}
